import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { LogOut, User as UserIcon } from "lucide-react";

import type { CurrentUser } from "~/lib/auth";
import { useLogout } from "~/lib/auth";

interface UserMenuProps {
    user: CurrentUser;
}

/**
 * Compact identity affordance in the top-right of the app shell.
 * Shows the user's initial in an accent chip; opens to email + role chips
 * + a sign-out action.
 */
export function UserMenu({ user }: UserMenuProps): React.ReactElement {
    const logout = useLogout();
    const initial = user.email.slice(0, 1).toUpperCase();

    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="press-crush"
                    aria-label="Open user menu"
                    style={{
                        width: 28,
                        height: 28,
                        borderRadius: "var(--radius-pill)",
                        background: "var(--color-accent-soft)",
                        color: "var(--color-accent)",
                        fontSize: "var(--font-size-13)",
                        fontWeight: "var(--font-weight-semibold)",
                        border: "var(--border-hairline) solid var(--color-border)",
                        cursor: "pointer",
                    }}
                >
                    {initial}
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    align="end"
                    sideOffset={6}
                    className="surface-raised enter-up z-popover"
                    style={{ minWidth: 240, padding: "var(--space-1)" }}
                >
                    <div className="stack-xs" style={{ padding: "var(--space-3) var(--space-3) var(--space-2)" }}>
                        <div className="cluster-xs">
                            <UserIcon size={14} className="text-text-subtle" />
                            <span className="text-13" style={{ wordBreak: "break-all" }}>{user.email}</span>
                        </div>
                        <div className="cluster-xs">
                            {user.roles.map((r) => (
                                <span key={r} className="chip chip--accent">{r}</span>
                            ))}
                        </div>
                    </div>

                    <DropdownMenu.Separator className="divider" style={{ margin: "var(--space-1) 0" }} />

                    <DropdownMenu.Item
                        onSelect={() => logout.mutate()}
                        className="cmdbar__item"
                        disabled={logout.isPending}
                    >
                        <LogOut size={14} aria-hidden />
                        <span>{logout.isPending ? "Signing out…" : "Sign out"}</span>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
